import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link } from '../router.js'
import { Drawer, Select } from '../admin/primitives.js'
import { jourFr, heureFr, plageHoraire } from '../soirees/lib.js'
import { LigneCaisseNuit, useCaisseNuit } from '../soirees/CaisseNuit.js'
import {
  etatAffichage, ligneCaisse, nuitDeLaSoiree, plageDesDates, textesCaisse, type ReponseCaisse,
} from '../soirees/caisse-nuit-model.js'
import { libelleSousLigne, nombreAffiche, prefixeSoireeDe, totalToutesBilletteries, type ChampsRattachement } from '../soirees/rattachement-model.js'
import { NomsAssociations, SousLigneRattachees } from '../soirees/Rattachements.js'
import { useMenuSoiree } from '../soirees/menu-soiree.js'

/**
 * /calendrier — CALENDRIER GLOBAL DE GESTION (lecture seule).
 *
 * Module de Gestion, pas de Soirées : sa v1 affiche les soirées du domaine
 * canonique, son architecture accepte d'autres objets plus tard. AUCUNE
 * création manuelle — pas de bouton « + ». Chaque fenêtre affichée est
 * demandée au serveur (jamais tout l'historique pour montrer un mois).
 */

/* Une case par NUIT (19/10/2026) : les événements d'associations rattachés
   à une soirée du club vivent dans sa case (son nombre, sa sous-ligne). */
interface Entry extends ChampsRattachement {
  id: string; name: string; starts_at: string; ends_at: string | null
  day: string; phase: 'UPCOMING' | 'ONGOING' | 'FINISHED' | 'CANCELLED'
  family: string | null
  tickets_valid: number | null; tickets_scanned: number | null
  cancelled: boolean
}

/** « + MEDECINE (Pretix) · rattachée automatiquement » — en texte seul (la ligne est déjà un bouton). */
function SousLigneTexte({ e }: { e: Entry }) {
  const r = e.rattachees_resume ?? []
  if (r.length === 0) return null
  return <span className="so-row__when so-ratt-sous__texte">{r.map(libelleSousLigne).join(' · ')}</span>
}
interface QuickView {
  soiree: { id: string; name: string; starts_at: string; ends_at: string | null
    phase: string; phase_label: string; family: string | null
    tickets_valid: number | null; tickets_scanned: number | null; scan_rate: number | null }
  coverage: { source: string; label: string; state: string; message: string | null }[]
  forecast: { range_low: number; range_high: number; confidence: string
    reference_quality: string; family_refs_count: number } | null
  outcome: { predicted_low: number; predicted_high: number; actual_scans: number
    inside_range: boolean; percentage_error: number | null } | null
}

type ViewMode = 'month' | 'week' | 'agenda'

const VIEW_OPTIONS = [
  { value: 'month', label: 'Mois' },
  { value: 'week', label: 'Semaine' },
  { value: 'agenda', label: 'Agenda' },
]
const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
/** Au-delà, la cellule renvoie vers le détail du jour plutôt que de grandir. */
const MAX_PER_CELL = 2
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet',
  'août', 'septembre', 'octobre', 'novembre', 'décembre']

async function fetchJson<T>(url: string): Promise<T | null> {
  try { const r = await fetch(url, { credentials: 'same-origin' }); return r.ok ? (await r.json()) as T : null }
  catch { return null }
}

const isoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** Grille du mois : lundi → dimanche, semaines complètes. */
function monthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month - 1, 1)
  const start = new Date(first)
  start.setDate(first.getDate() - ((first.getDay() + 6) % 7))
  const days: Date[] = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    days.push(d)
    if (i >= 34 && d.getMonth() !== month - 1 && (d.getDay() + 6) % 7 === 6) break
  }
  return days
}

function weekGrid(anchor: Date): Date[] {
  const start = new Date(anchor)
  start.setDate(anchor.getDate() - ((anchor.getDay() + 6) % 7))
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start); d.setDate(start.getDate() + i); return d
  })
}

export function CalendarPage() {
  const meta = useShellMeta()
  const today = new Date()
  const [view, setView] = useState<ViewMode>(window.innerWidth < 760 ? 'agenda' : 'month')
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1))
  const [entries, setEntries] = useState<Entry[] | null>(null)
  const [dir, setDir] = useState<'next' | 'prev'>('next')
  const [selected, setSelected] = useState<Entry | null>(null)
  const [quick, setQuick] = useState<QuickView | null>(null)
  const [dayOpen, setDayOpen] = useState<string | null>(null)
  const menuSoiree = useMenuSoiree()

  const window_ = useMemo(() => {
    if (view === 'month') {
      return { year: cursor.getFullYear(), month: cursor.getMonth() + 1, from: null, to: null }
    }
    if (view === 'week') {
      const w = weekGrid(cursor)
      return { year: null, month: null, from: isoDay(w[0]!), to: isoDay(w[6]!) }
    }
    // agenda : 60 jours glissants depuis le curseur
    const to = new Date(cursor); to.setDate(cursor.getDate() + 60)
    return { year: null, month: null, from: isoDay(cursor), to: isoDay(to) }
  }, [view, cursor])

  // Après un « Défaire » dans la fiche rapide : la case se relit tout de suite.
  const [relire, setRelire] = useState(0)
  const fenetreLue = useRef<typeof window_ | null>(null)
  useEffect(() => {
    let vivant = true
    // Une autre fenêtre : le squelette ; une relecture de la même : les cases restent.
    if (fenetreLue.current !== window_) setEntries(null)
    fenetreLue.current = window_
    const p = new URLSearchParams()
    if (window_.year) { p.set('year', String(window_.year)); p.set('month', String(window_.month)) }
    else { p.set('from', window_.from!); p.set('to', window_.to!) }
    void fetchJson<{ entries: Entry[] }>(`/api/v1/calendar?${p}`)
      .then((j) => { if (vivant) setEntries(j?.entries ?? []) })
    return () => { vivant = false }
  }, [window_, relire])

  /* LA CAISSE DE LA NUIT (15/10/2026) — une lecture pour la fenêtre
     affichée. Discrète par choix : la cellule reste aux soirées (la bulle de
     la soirée porte les chiffres), la fiche rapide et l'agenda les écrivent
     en petit, sous la soirée. */
  const plageCaisse = useMemo(() => plageDesDates((entries ?? []).map((e) => e.day)), [entries])
  const caisse = useCaisseNuit('calendar', plageCaisse)

  const byDay = useMemo(() => {
    const m = new Map<string, Entry[]>()
    for (const e of entries ?? []) {
      const arr = m.get(e.day) ?? []
      arr.push(e)
      m.set(e.day, arr)
    }
    return m
  }, [entries])

  const openEntry = useCallback((e: Entry) => {
    setSelected(e)
    setQuick(null)
    void fetchJson<QuickView>(`/api/v1/calendar/events/${e.id}`).then(setQuick)
  }, [])

  const move = (delta: number) => {
    setDir(delta > 0 ? 'next' : 'prev')
    const d = new Date(cursor)
    if (view === 'month') d.setMonth(cursor.getMonth() + delta)
    else if (view === 'week') d.setDate(cursor.getDate() + delta * 7)
    else d.setDate(cursor.getDate() + delta * 30)
    setCursor(d)
  }

  const title = view === 'month'
    ? `${MOIS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : view === 'week'
      ? `Semaine du ${jourFr(weekGrid(cursor)[0]!.toISOString())}`
      : `À partir du ${jourFr(cursor.toISOString())}`

  const days = view === 'month' ? monthGrid(cursor.getFullYear(), cursor.getMonth() + 1) : weekGrid(cursor)

  return (
    <Shell active="calendrier" meta={meta}>
      <main className="lc-page lc-stagger cal-page">
        <div className="so-section-head">
          <div>
            <h1 className="lc-title"><CalendarDays size={26} aria-hidden="true" /> Calendrier</h1>
            <p className="lc-subtitle">Les soirées du Live Club — vue d'ensemble en lecture.</p>
          </div>
          <div className="so-toolbar so-toolbar--inline">
            <span className="cal-nav">
              <button type="button" className="lc-btn lc-btn--icon" onClick={() => move(-1)}
                aria-label="Période précédente"><ChevronLeft size={16} /></button>
              <button type="button" className="lc-btn" onClick={() => { setDir('next'); setCursor(new Date(today.getFullYear(), today.getMonth(), view === 'month' ? 1 : today.getDate())) }}>
                Aujourd'hui
              </button>
              <button type="button" className="lc-btn lc-btn--icon" onClick={() => move(1)}
                aria-label="Période suivante"><ChevronRight size={16} /></button>
            </span>
            <Select compact value={view} options={VIEW_OPTIONS} ariaLabel="Vue du calendrier"
              onChange={(v) => setView(v as ViewMode)} />
          </div>
        </div>

        <h2 className="cal-title" aria-live="polite">{title}</h2>

        {entries === null ? (
          <div className="cal-skeleton" aria-busy="true">
            {Array.from({ length: view === 'agenda' ? 5 : 14 }, (_, i) => <span key={i} />)}
          </div>
        ) : view === 'agenda' ? (
          <AgendaView entries={entries} onOpen={openEntry} caisse={caisse} />
        ) : (
          <div className={`cal-grid cal-grid--${view} cal-grid--${dir}`} key={`${view}-${cursor.toISOString()}`}>
            {JOURS.map((j) => <div key={j} className="cal-dow" aria-hidden="true">{j}</div>)}
            {days.map((d) => {
              const key = isoDay(d)
              const list = byDay.get(key) ?? []
              const isToday = key === isoDay(today)
              const outside = view === 'month' && d.getMonth() !== cursor.getMonth()
              return (
                <div key={key} className={`cal-cell${outside ? ' cal-cell--out' : ''}${isToday ? ' cal-cell--today' : ''}`}>
                  <span className="cal-cell__num">{d.getDate()}</span>
                  {/* Une journée chargée montre les premiers, puis « +N autres »
                      qui ouvre le détail du jour — jamais une cellule qui pousse. */}
                  {list.slice(0, MAX_PER_CELL).map((e) => (
                    <button key={e.id} type="button" className={`cal-chip cal-chip--${e.cancelled ? 'cancelled' : e.phase.toLowerCase()}`} {...menuSoiree.soiree(e, caisse)}
                      onClick={() => openEntry(e)} title={bulleCaisse(caisse, e)}
                      aria-label={`${e.name}, ${jourFr(e.starts_at)} — voir le détail`}>
                      <span className="cal-chip__name">{e.name}</span>
                      {nombreAffiche(e) !== null ? <span className="cal-chip__kpi">{nombreAffiche(e)}</span> : null}
                    </button>
                  ))}
                  {list.length > MAX_PER_CELL ? (
                    <button type="button" className="cal-more" onClick={() => setDayOpen(key)}
                      aria-label={`${list.length - MAX_PER_CELL} autre(s) soirée(s) le ${key} — voir la journée`}>
                      +{list.length - MAX_PER_CELL} autre{list.length - MAX_PER_CELL > 1 ? 's' : ''}
                    </button>
                  ) : null}
                </div>
              )
            })}
          </div>
        )}

        {entries !== null && entries.length === 0 ? (
          <p className="gd-calm">Aucune soirée sur cette période.</p>
        ) : null}

        <p className="lc-hint so-source">
          Lecture seule — les soirées viennent du domaine canonique (billetterie).
        </p>

        <Drawer open={dayOpen !== null} title={dayOpen ? jourFr(`${dayOpen}T12:00:00Z`) : ''} onClose={() => setDayOpen(null)}>
          <div className="cal-day-list">
            {(byDay.get(dayOpen ?? '') ?? []).map((e) => (
              <button key={e.id} type="button" className="so-row so-clickable" {...menuSoiree.soiree(e, caisse)}
                onClick={() => { setDayOpen(null); openEntry(e) }}
                aria-label={`${e.name} — voir le détail`}>
                <span className={`cal-dot cal-dot--${e.cancelled ? 'cancelled' : e.phase.toLowerCase()}`} aria-hidden="true" />
                <span className="so-row__body">
                  <span className="so-row__name">{e.name}</span>
                  <span className="so-row__when">{heureFr(e.starts_at)}{e.family ? ` · ${e.family}` : ''}</span>
                  <SousLigneTexte e={e} />
                  <LigneCaisseNuit r={caisse} soireeId={e.id} />
                </span>
                {nombreAffiche(e) !== null ? (
                  <span className="so-row__scans"><strong>{nombreAffiche(e)}</strong><span>scannés</span></span>
                ) : null}
              </button>
            ))}
          </div>
        </Drawer>

        <Drawer open={selected !== null} title={selected?.name ?? ''} onClose={() => { setSelected(null); setQuick(null) }}>
          {selected ? (
            quick === null ? (
              <div className="so-skel" aria-busy="true"><span /><span /><span /></div>
            ) : (
              <>
                <dl className="lc-kv">
                  <div><dt>Quand</dt><dd>{jourFr(quick.soiree.starts_at)} · {plageHoraire(quick.soiree.starts_at, quick.soiree.ends_at)}</dd></div>
                  <div><dt>État</dt><dd>{quick.soiree.phase_label}</dd></div>
                  {quick.soiree.family ? <div><dt>Famille</dt><dd>{quick.soiree.family}</dd></div> : null}
                  {quick.soiree.tickets_valid !== null ? (
                    <div><dt>Billets</dt><dd>
                      {quick.soiree.tickets_valid} vendus
                      {quick.soiree.tickets_scanned !== null ? ` · ${quick.soiree.tickets_scanned} scannés` : ''}
                      {quick.soiree.scan_rate !== null ? ` · ${quick.soiree.scan_rate.toLocaleString('fr-FR')} %` : ''}
                    </dd></div>
                  ) : null}
                </dl>

                {(selected.rattachees_resume ?? []).length > 0 ? (
                  <div className="so-factors">
                    <h3 className="so-factors__title">Billetteries de la soirée</h3>
                    <p style={{ margin: 0 }}>
                      {/* Le total de toutes les billetteries, quel que soit le réglage du nombre affiché. */}
                      {totalToutesBilletteries({ tickets_scanned: quick.soiree.tickets_scanned, rattachees: selected.rattachees }) ?? 0} entrées toutes billetteries, dont {quick.soiree.tickets_scanned ?? 0} pour la soirée elle-même.
                    </p>
                    <SousLigneRattachees resume={selected.rattachees_resume} onChange={() => { setSelected(null); setQuick(null); setRelire((x) => x + 1) }} />
                  </div>
                ) : null}
                {/* Qui organise (20/10/2026) : dans le tiroir seulement — la case du calendrier est déjà un bouton. */}
                {selected.association ? (
                  <p className="lc-hint">{prefixeSoireeDe(selected.association.name)}<NomsAssociations associations={[selected.association]} prefixe="" /></p>
                ) : null}
                {selected.rattachee_a ? (
                  <p className="lc-hint">Rattachée à « {selected.rattachee_a.name} » : ses entrées comptent avec cette soirée.</p>
                ) : null}

                {quick.forecast ? (
                  <div className="so-factors">
                    <h3 className="so-factors__title">Prévision</h3>
                    <p style={{ margin: 0 }}>
                      <strong>{quick.forecast.range_low}–{quick.forecast.range_high}</strong> scans projetés ·
                      confiance {quick.forecast.confidence.toLowerCase()}
                      {quick.forecast.family_refs_count > 0
                        ? ` · ${quick.forecast.family_refs_count} référence(s) de la même famille` : ''}
                    </p>
                  </div>
                ) : null}

                {quick.outcome ? (
                  <div className="so-factors">
                    <h3 className="so-factors__title">Prévision vs réel</h3>
                    <p style={{ margin: 0 }}>
                      Prévu {quick.outcome.predicted_low}–{quick.outcome.predicted_high} ·
                      réel <strong>{quick.outcome.actual_scans}</strong> ·{' '}
                      <span className={quick.outcome.inside_range ? 'so-up' : 'so-down'}>
                        {quick.outcome.inside_range ? 'dans la fourchette' : 'hors fourchette'}
                      </span>
                    </p>
                  </div>
                ) : null}

                <CaisseDeLaFicheRapide r={caisse} soireeId={selected.id} />

                {quick.coverage.filter((c) => c.state === 'NOT_YET_AVAILABLE').length > 0 ? (
                  <div className="so-factors">
                    <h3 className="so-factors__title">Sources de l'époque</h3>
                    <ul>
                      {quick.coverage.filter((c) => c.state === 'NOT_YET_AVAILABLE').map((c) => (
                        <li key={c.source}>{c.label} — pas encore en service à cette date</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <p style={{ marginTop: 16 }}>
                  <Link href={`/soirees/${selected.id}`} className="lc-btn lc-btn--primary" {...menuSoiree.soiree(selected, caisse)}>
                    <Sparkles size={14} aria-hidden="true" /> Voir la soirée
                  </Link>
                </p>
              </>
            )
          ) : null}
        </Drawer>
      </main>
    </Shell>
  )
}

/** La bulle d'une soirée du mois : son nom, puis la caisse de sa nuit quand elle a quelque chose à dire. */
function bulleCaisse(r: ReponseCaisse | null, e: Entry): string {
  const n = nuitDeLaSoiree(r, e.id)
  if (r === null || n === null || etatAffichage(r, n) !== 'chiffres') return e.name
  const t = textesCaisse(r, n)
  return `${e.name}\n${t.nuitPartagee ? `${t.nuitPartagee} · ` : ''}${ligneCaisse(t)}`
}

/** Dans la fiche rapide d'une soirée : la caisse de sa nuit, en petit, et le chemin vers la fiche de nuit. */
function CaisseDeLaFicheRapide({ r, soireeId }: { r: ReponseCaisse | null; soireeId: string }) {
  const n = nuitDeLaSoiree(r, soireeId)
  if (r === null || n === null || etatAffichage(r, n) !== 'chiffres') return null
  return (
    <div className="so-factors">
      <h3 className="so-factors__title">Caisse de la nuit</h3>
      {/* La ligne est un bloc (`.so-caisse`) : le lien vit dans son propre
          paragraphe, dessous — collé derrière, il tombait seul à la ligne
          suivante, en commençant par « · ». */}
      <LigneCaisseNuit r={r} soireeId={soireeId} />
      <p className="so-caisse__lien">
        <Link href={`/comptabilite/nuits/${n.business_date}`}>Fiche de la nuit</Link>
      </p>
    </div>
  )
}

function AgendaView({ entries, onOpen, caisse }: { entries: Entry[]; onOpen: (e: Entry) => void; caisse: ReponseCaisse | null }) {
  const menuSoiree = useMenuSoiree()
  if (entries.length === 0) return null
  return (
    <div className="lc-panel gd-panel"><div className="lc-panel__body lc-panel__body--flush so-rows">
      {entries.map((e) => (
        <button key={e.id} type="button" className="so-row so-clickable" onClick={() => onOpen(e)} {...menuSoiree.soiree(e, caisse)}
          aria-label={`${e.name} — voir le détail`}>
          <span className={`cal-dot cal-dot--${e.cancelled ? 'cancelled' : e.phase.toLowerCase()}`} aria-hidden="true" />
          <span className="so-row__body">
            <span className="so-row__name">{e.name}</span>
            <span className="so-row__when">{jourFr(e.starts_at)} · {heureFr(e.starts_at)}</span>
            <SousLigneTexte e={e} />
            <LigneCaisseNuit r={caisse} soireeId={e.id} />
          </span>
          {nombreAffiche(e) !== null ? (
            <span className="so-row__scans"><strong>{nombreAffiche(e)}</strong><span>scannés</span></span>
          ) : null}
        </button>
      ))}
    </div></div>
  )
}
